import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Briefcase, CheckCircle, DollarSign, Award, Mail, Loader2, RefreshCw } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";

const AgentRegisterPage = () => {
  const navigate = useNavigate();
  const { registerAgent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    country: '',
    city: '',
    years_experience: 0,
    british_council_certified: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await registerAgent({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        company_name: formData.company_name,
        country: formData.country,
        city: formData.city,
        years_experience: parseInt(formData.years_experience) || 0,
        british_council_certified: formData.british_council_certified
      });
      // Show verification sent message instead of navigating
      setSubmittedEmail(formData.email);
      setVerificationSent(true);
      toast.success('Please check your email to verify your account');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Show verification sent screen
  if (verificationSent) {
    const handleResend = async () => {
      try {
        setResending(true);
        const res = await authAPI.resendAgentVerification(submittedEmail);
        if (res.data?.email_sent === false) {
          toast.info(res.data?.message || 'If a pending registration exists, a fresh link was sent.');
        } else {
          toast.success('A fresh verification email has been sent. Please check your inbox and spam folder.');
        }
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Could not resend the email right now. Please try again in a minute.');
      } finally {
        setResending(false);
      }
    };

    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md text-center shadow-xl">
          <div className="w-20 h-20 mx-auto bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mb-6">
            <Mail className="w-10 h-10 text-teal-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Verify Your Email</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            We've sent a verification email to:
          </p>
          <p className="font-semibold text-teal-600 dark:text-teal-400 mb-6">{submittedEmail}</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            Please check your inbox and click the verification link to complete your registration. The link will expire in 24 hours.
          </p>
          <div className="space-y-3">
            <Button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-70"
              data-testid="agent-resend-verification"
            >
              {resending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending fresh link…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full border-gray-300 dark:border-slate-600 dark:text-gray-200 dark:hover:bg-slate-700">
                Back to Login
              </Button>
            </Link>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-6">
            Didn't receive the email? Check your spam folder, or click <strong>Resend</strong> above. If the issue persists, contact <a className="text-teal-600 hover:underline" href="mailto:info@hedirect.org">info@hedirect.org</a>.
          </p>
        </div>
      </div>
    );
  }

  const benefits = [
    { icon: DollarSign, text: "Competitive commission - the more you recruit, the more you earn" },
    { icon: CheckCircle, text: "Access to 106+ partner universities worldwide" },
    { icon: CheckCircle, text: "Dedicated CRM for student management" },
    { icon: CheckCircle, text: "Training & dedicated support team" }
  ];

  const countries = [
    "Pakistan", "India", "Bangladesh", "Nepal", "Sri Lanka", "Nigeria", 
    "Ghana", "Kenya", "UAE", "Saudi Arabia", "Other"
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 flex transition-colors">
      {/* Left Side - Benefits */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#1E3A5F] dark:from-slate-800 to-[#f8f9fa] dark:to-slate-900 p-12 items-center">
        <div className="max-w-lg">
          <Link to="/">
            <img src={LOGO_URL} alt="H E Direct" className="h-16 mb-8" />
          </Link>
          <h2 className="text-3xl font-semibold text-gray-800 dark:text-white mb-4">
            Become a Partner Agent
          </h2>
          <div className="flex items-center gap-3 mb-8">
            <span className="text-3xl font-semibold text-teal-600 dark:text-teal-400">Competitive</span>
            <span className="text-xl text-gray-600 dark:text-gray-300">Commission Rates</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
            Join our network of education agents and help students achieve their study abroad dreams. The more students you place, the higher your earnings.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                <benefit.icon className="w-5 h-5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                {benefit.text}
              </li>
            ))}
          </ul>
          
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-4 text-center">
              <div className="text-2xl font-semibold text-teal-600 dark:text-teal-400">100+</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Partner Universities</div>
            </div>
            <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-4 text-center">
              <div className="text-2xl font-semibold text-teal-600 dark:text-teal-400">500+</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Students Placed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center lg:hidden">
            <Link to="/">
              <img src={LOGO_URL} alt="H E Direct" className="h-12 mx-auto mb-4" />
            </Link>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-6 h-6 text-teal-600" />
              <h1 className="text-3xl font-semibold text-gray-800 dark:text-white">Agent Registration</h1>
            </div>
            <p className="text-gray-500 dark:text-gray-400">Join our partner network today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-gray-600 dark:text-gray-300">Full Name</Label>
                <Input
                  id="full_name"
                  type="text"
                  placeholder="Your name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                  required
                  data-testid="agent-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-600 dark:text-gray-300">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+92 300 123 4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                  required
                  data-testid="agent-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-600 dark:text-gray-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                required
                data-testid="agent-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-gray-600 dark:text-gray-300">Company/Agency Name</Label>
              <Input
                id="company_name"
                type="text"
                placeholder="Your agency name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                required
                data-testid="agent-company"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country" className="text-gray-600 dark:text-gray-300">Country</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white" data-testid="agent-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#f1f3f5] dark:bg-slate-800 border-gray-200 dark:border-slate-700">
                    {countries.map((country) => (
                      <SelectItem key={country} value={country} className="text-gray-700 dark:text-gray-200 hover:bg-[#e9ecef] dark:hover:bg-slate-700">
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="text-gray-600 dark:text-gray-300">City</Label>
                <Input
                  id="city"
                  type="text"
                  placeholder="Your city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                  required
                  data-testid="agent-city"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="years_experience" className="text-gray-600 dark:text-gray-300">Years of Experience</Label>
              <Select 
                value={formData.years_experience.toString()} 
                onValueChange={(value) => setFormData({ ...formData, years_experience: value })}
              >
                <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white" data-testid="agent-experience">
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent className="bg-[#f1f3f5] dark:bg-slate-800 border-gray-200 dark:border-slate-700">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-gray-700 dark:text-gray-200 hover:bg-[#e9ecef] dark:hover:bg-slate-700">
                      {year === 0 ? 'New to industry' : year === 10 ? '10+ years' : `${year} years`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* British Council Certification Question */}
            <div className="space-y-3 p-4 bg-[#e9ecef]/50 dark:bg-slate-700/40 rounded-lg border border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                <Label className="text-gray-700 dark:text-gray-200 font-medium">Are you a British Council Certified Agent?</Label>
              </div>
              <RadioGroup
                value={formData.british_council_certified ? "yes" : "no"}
                onValueChange={(value) => setFormData({ ...formData, british_council_certified: value === "yes" })}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="yes" 
                    id="bc-yes" 
                    className="border-teal-500 text-teal-600"
                    data-testid="bc-certified-yes"
                  />
                  <Label htmlFor="bc-yes" className="text-gray-600 dark:text-gray-300 cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="no" 
                    id="bc-no"
                    className="border-slate-500"
                    data-testid="bc-certified-no"
                  />
                  <Label htmlFor="bc-no" className="text-gray-600 dark:text-gray-300 cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {formData.british_council_certified && (
                <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Great! British Council certified agents get priority support.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-600 dark:text-gray-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 pr-10 dark:text-white"
                    required
                    data-testid="agent-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-600 dark:text-gray-300">Confirm</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 h-11 dark:text-white"
                  required
                  data-testid="agent-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold h-12 rounded-none"
              data-testid="agent-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Register as Agent
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          <div className="text-center space-y-3">
            <p className="text-gray-500 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:underline font-medium">
                Sign in
              </Link>
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              Are you a student?{' '}
              <Link to="/register" className="text-teal-600 hover:underline font-medium">
                Register as Student
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentRegisterPage;
